<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { ContractFilesService } from '@/services/contractManagement/contractFilesService';
import ContractFileUpload from '@/components/contractManagement/ContractFileUpload.vue';
import VaultFileBrowser from '@/components/file_vault/VaultFileBrowser.vue';
import type { VaultBrowserItemState } from '@/components/file_vault/vaultFileBrowser';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaTabs from '@/components/design/base/GaiaTabs.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

/**
 * Adds a document to an existing contract: a new upload goes straight into
 * it, or a plain file from the vault is filed into it (it leaves its folder).
 * Either way the AI classifies the new contract file — its type and
 * description are not asked for here.
 *
 * Filing an existing file needs ADMIN on it; the backend refuses otherwise,
 * and with a 409 when the file already belongs to a contract.
 */
const props = defineProps<{
  show: boolean;
  contractUid: string;
}>();

const emit = defineEmits<{
  'update:show': [value: boolean];
  created: [];
}>();

const message = useGaiaToast();

type Source = 'upload' | 'select';

const source = ref<Source>('upload');
const uploadRef = ref<InstanceType<typeof ContractFileUpload>>();
const selectedFileKeys = ref<string[]>([]);
const saving = ref(false);

/** Only plain files can be filed; contracts stay hidden in the browser. */
const fileState: VaultBrowserItemState = item =>
  item.vaultFile?.converting
    ? { disabled: true, reason: 'Still being converted — try again in a moment' }
    : null;

const selectedFileUid = computed(() => {
  const key = selectedFileKeys.value.find(k => k.startsWith('file-'));
  return key ? key.slice('file-'.length) : null;
});

const canSave = computed(
  () =>
    !saving.value &&
    (source.value === 'upload'
      ? (uploadRef.value?.hasFile ?? false)
      : selectedFileUid.value !== null)
);

function close(): void {
  if (!saving.value) emit('update:show', false);
}

watch(
  () => props.show,
  show => {
    if (show) return;
    source.value = 'upload';
    selectedFileKeys.value = [];
    uploadRef.value?.clearSelection();
  }
);

async function save(): Promise<void> {
  if (!canSave.value) return;
  saving.value = true;
  try {
    if (source.value === 'upload') {
      await uploadRef.value?.uploadSelectedFile();
      message.success('Uploaded — the file is being classified');
    } else if (selectedFileUid.value) {
      await ContractFilesService.createContractFile(props.contractUid, selectedFileUid.value);
      message.success('Added to the contract — the file is being classified');
    }
    emit('created');
    emit('update:show', false);
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to add the file');
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <gaia-modal
    :show="props.show"
    width="800px"
    title="Add a file"
    :mask-closable="!saving"
    :close-on-esc="!saving"
    :closable="!saving"
    @update:show="(value: boolean) => !value && close()"
  >
    <n-flex vertical :size="14" :wrap="false">
      <gaia-text size="control" tone="subtle">
        Upload a document or pick a file from the vault. Its type and description are read from the
        document once it is in the contract.
      </gaia-text>
      <gaia-tabs
        :value="source"
        :style="{ width: 'fit-content' }"
        @update:value="(value: Source) => !saving && (source = value)"
      >
        <n-tab name="upload">Upload</n-tab>
        <n-tab name="select">Select existing</n-tab>
      </gaia-tabs>
      <contract-file-upload
        v-show="source === 'upload'"
        ref="uploadRef"
        :contract-uid="props.contractUid"
        accept=".pdf,.docx,.doc,.txt"
      />
      <!-- Bounded height so the browser's infinite scroll has a real scroll
           viewport (otherwise it reveals every row). -->
      <vault-file-browser
        v-if="source === 'select'"
        v-model:selected-keys="selectedFileKeys"
        single-selection
        file-only-selection
        :get-item-selection-state="fileState"
        :disabled="saving"
        :style="{ height: '50vh', minHeight: '360px' }"
      />
    </n-flex>

    <template #action>
      <n-flex justify="end" :size="8" :wrap="false">
        <gaia-button :disabled="saving" @click="close">Cancel</gaia-button>
        <gaia-button type="primary" :loading="saving" :disabled="!canSave" @click="save">
          Add to contract
        </gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>
